import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import TaskService from "service/TaskService";
import { FieldTimeLog } from "domain/model/SummaryPageParams";

const TaskFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sId = searchParams.get("sId");
  const formCode = searchParams.get("formCode");
  const navigate = useNavigate();
  const location = useLocation();

  const isDemoMode = !!formCode;

  const [formData, setFormData] = useState<any>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sessionData, setSessionData] = useState<{ startDate: string; sessionId: string }>({
    startDate: "",
    sessionId: "",
  });

  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [activeCameraField, setActiveCameraField] = useState<string | null>(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [aspectRatioKey, setAspectRatioKey] = useState<'9:16' | '1:1' | '16:9'>('1:1');
  const [selectedFieldRatio, setSelectedFieldRatio] = useState<string | undefined>();

  const parseAllowedAspectRatios = (allowedStr?: string): ('9:16' | '1:1' | '16:9')[] => {
    if (!allowedStr || !allowedStr.trim()) {
      return ['9:16', '1:1', '16:9'];
    }
    const parts = allowedStr.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (parts.length === 0) {
      return ['9:16', '1:1', '16:9'];
    }
    const result: ('9:16' | '1:1' | '16:9')[] = [];
    if (parts.includes('portrait')) result.push('9:16');
    if (parts.includes('square')) result.push('1:1');
    if (parts.includes('landscape')) result.push('16:9');

    return result.length > 0 ? result : ['9:16', '1:1', '16:9'];
  };

  const ASPECT_RATIOS: { key: '9:16' | '1:1' | '16:9'; label: string; ratio: number }[] = [
    { key: '9:16', label: 'PORTRAIT', ratio: 9 / 16 },
    { key: '1:1', label: 'SQUARE', ratio: 1 },
    { key: '16:9', label: 'LANDSCAPE', ratio: 16 / 9 },
  ];

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
  const [dynamicMinValues, setDynamicMinValues] = useState<Record<string, number>>({});

  const lastSubmitTapRef = useRef(0);
  const SUBMIT_THROTTLE_MS = 1000;

  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [pageStack, setPageStack] = useState<string[]>([]);
  const [visitedPages, setVisitedPages] = useState<string[]>([]);
  const [visitedFields, setVisitedFields] = useState<Set<string>>(new Set());

  const currentPage = formData?.pages?.find((p: any) => p.id === currentPageId);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("left");

  const [fieldTimeLogs, setFieldTimeLogs] = useState<FieldTimeLog[]>([]);
  const [isFormReady, setIsFormReady] = useState(false);
  const isFormReadyRef = useRef(false);

  const nextFieldStartTimeRef = useRef<number | null>(null);
  const currentActiveFieldIdRef = useRef<string | null>(null);
  const serverStartTimestampRef = useRef<number>(0);
  const deviceTimeAtLoadRef = useRef<number>(0);

  const multiSelectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const multiSelectFieldIdRef = useRef<string | null>(null);
  const multiSelectStartTimeRef = useRef<number | null>(null);

  const [showMethodModal, setShowMethodModal] = useState(false);
  const [methodFieldId, setMethodFieldId] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [previewAttachmentUrl, setPreviewAttachmentUrl] = useState<string | null>(null);

  const [customOptions, setCustomOptions] = useState<Record<string, string[]>>({});
  const [customInputTexts, setCustomInputTexts] = useState<Record<string, string>>({});
  const [dropdownModalData, setDropdownModalData] = useState<{
    field: any;
    displayOptions: string[];
    mergedCustomOptions: string[];
    isAddRemoveEnabled: boolean;
    isMultiple?: boolean;
  } | null>(null);

  const openDropdownModal = (
    field: any,
    displayOptions: string[],
    mergedCustomOptions: string[],
    isAddRemoveEnabled: boolean,
    isMultiple?: boolean,
  ) => {
    setDropdownModalData({
      field,
      displayOptions,
      mergedCustomOptions,
      isAddRemoveEnabled,
      isMultiple: !!isMultiple,
    });
  };

  const [tempImageSource, setTempImageSource] = useState<'in_apps_camera' | 'storage' | null>(null);
  const [photoMethods, setPhotoMethods] = useState<Record<string, ('in_apps_camera' | 'storage')[]>>({});

  const [moreAccessTaskId, setMoreAccessTaskId] = useState<string | null>(null);
  const [moreAccessCoords, setMoreAccessCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [startLocation, setStartLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationStatus, setLocationStatus] = useState<"idle" | "requesting" | "granted" | "denied">("idle");
  const [lockSubmitDistance, setLockSubmitDistance] = useState<boolean>(true);

  const tId = searchParams.get("tId");
  const isMoreAccessMode = !!tId;
  const consentCodeParam = searchParams.get("consentCode") || undefined;
  const consentValueParam = searchParams.get("consentValue") === "true" ? true : searchParams.get("consentValue") === "false" ? false : undefined;
  const consentAttachmentUrlParam = searchParams.get("consentAttachmentUrl") || undefined;

  const taskService = TaskService();

  const getDurationNow = (): number => {
    const deviceElapsed = Date.now() - deviceTimeAtLoadRef.current;
    return serverStartTimestampRef.current + deviceElapsed;
  };

  const validatePhoneNumber = (text: string) => {
    let cleaned = text.replace(/\D/g, '');
    if (cleaned.startsWith('62')) {
      cleaned = '0' + cleaned.substring(2);
    }
    return cleaned;
  };

  const findSourceField = (targetFieldId: string) => {
    if (!formData?.pages) return null;
    const regex = new RegExp(`matrix_target\\(\\s*["']${targetFieldId}["']\\s*\\)`);
    for (const page of formData.pages) {
      if (page.fields) {
        for (const f of page.fields) {
          if (f.actionRules && regex.test(f.actionRules)) {
            return f;
          }
        }
      }
    }
    return null;
  };

  const handleSubFieldChange = (targetFieldId: string, subQuestionLabel: string, textValue: any, targetField: any) => {
    setValues(v => {
      const currentTargetValues = v[targetFieldId] || {};
      const updatedTargetValues = {
        ...currentTargetValues,
        [subQuestionLabel]: textValue
      };
      const updated = {
        ...v,
        [targetFieldId]: updatedTargetValues
      };
      if (targetField?.actionRules) {
        handleActionRules(targetField.actionRules, updated, "field");
      }
      return updated;
    });
  };

  const renderSubFieldInput = (
    targetFieldId: string,
    subQuestionLabel: string,
    subFieldTypeKey: number,
    subValue: any,
    targetField: any
  ) => {
    const subFieldId = `${targetFieldId}_matrixsub_${subQuestionLabel}`;

    switch (subFieldTypeKey) {
      case 2:
        return (
          <input
            type="number"
            className="form-control"
            value={subValue ? String(subValue) : ""}
            disabled={!isFormReady}
            onFocus={() => {
              if (!isDemoMode && isFormReadyRef.current) {
                currentActiveFieldIdRef.current = targetFieldId;
              }
            }}
            onBlur={() => {
              if (!isDemoMode && isFormReadyRef.current) {
                endFieldAndLog(targetFieldId);
                if (currentActiveFieldIdRef.current === targetFieldId) currentActiveFieldIdRef.current = null;
              }
            }}
            onChange={e => handleSubFieldChange(targetFieldId, subQuestionLabel, e.target.value, targetField)}
          />
        );

      case 3: {
        const currentPhotos = Array.isArray(subValue) ? subValue : subValue ? [subValue] : [];
        const isQuotaFull = currentPhotos.length >= 1;

        return (
          <div>
            {currentPhotos.length > 0 && (
              <div className="row g-2 mb-2">
                {currentPhotos.map((photoUrl: string, idx: number) => (
                  <div className="col-6 position-relative" key={idx}>
                    <img src={photoUrl} alt={`sub-preview-${idx}`} className="img-thumbnail w-100" style={{ aspectRatio: "1/1", objectFit: "cover" }} />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm position-absolute"
                      style={{ top: "4px", right: "8px", borderRadius: "50%", padding: "2px 8px" }}
                      onClick={() => {
                        const newPhotos = currentPhotos.filter((_: any, i: number) => i !== idx);
                        setValues(v => {
                          const targetValues = v[targetFieldId] || {};
                          const updatedTargetValues = {
                            ...targetValues,
                            [subQuestionLabel]: newPhotos,
                          };
                          const updated = { ...v, [targetFieldId]: updatedTargetValues };
                          if (targetField?.actionRules) handleActionRules(targetField.actionRules, updated, "field");
                          return updated;
                        });
                        const photoMethodKey = `${targetFieldId}_${subQuestionLabel}`;
                        setPhotoMethods(prev => ({
                          ...prev,
                          [photoMethodKey]: (prev[photoMethodKey] || []).filter((_, i) => i !== idx),
                        }));
                      }}
                    >&times;</button>
                  </div>
                ))}
              </div>
            )}

            {!isQuotaFull && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={!isFormReady}
                onClick={() => handlePicturePress(subFieldId, targetField.methodCamera ?? true, targetField.methodGallery ?? true, targetField.availableRatioCameraInApps)}
              >
                {currentPhotos.length > 0 ? "+ Tambah Foto Lain" : "Ambil Foto"}
              </button>
            )}
          </div>
        );
      }

      case 4:
        return (
          <input
            type="tel"
            className="form-control"
            value={subValue || ""}
            disabled={!isFormReady}
            onChange={e => {
              const validated = validatePhoneNumber(e.target.value);
              handleSubFieldChange(targetFieldId, subQuestionLabel, validated, targetField);
            }}
          />
        );

      default:
        return (
          <input
            type="text"
            className="form-control"
            value={subValue || ""}
            disabled={!isFormReady}
            onFocus={() => {
              if (!isDemoMode && isFormReadyRef.current) {
                currentActiveFieldIdRef.current = targetFieldId;
              }
            }}
            onBlur={() => {
              if (!isDemoMode && isFormReadyRef.current) {
                endFieldAndLog(targetFieldId);
                if (currentActiveFieldIdRef.current === targetFieldId) currentActiveFieldIdRef.current = null;
              }
            }}
            onChange={e => handleSubFieldChange(targetFieldId, subQuestionLabel, e.target.value, targetField)}
          />
        );
    }
  };

  const endFieldAndLog = (fieldId: string) => {
    if (isDemoMode) return;
    if (!isFormReadyRef.current) return;
    if (nextFieldStartTimeRef.current === null) return;

    const endTime = getDurationNow();
    const duration = Math.floor((endTime - nextFieldStartTimeRef.current) / 1000);

    if (duration > 0) {
      setFieldTimeLogs(prev => [...prev, {
        fieldId,
        endTimestamp: endTime,
        duration,
        isSummary: false,
      }]);
      console.log(`[endField] ${fieldId}: start=${new Date(nextFieldStartTimeRef.current!).toISOString()}, end=${new Date(endTime).toISOString()}, duration=${duration}s`);
    }

    nextFieldStartTimeRef.current = endTime;
  };

  const endMultiSelectAndLog = () => {
    if (multiSelectTimerRef.current) {
      clearTimeout(multiSelectTimerRef.current);
      multiSelectTimerRef.current = null;
    }

    if (multiSelectFieldIdRef.current && multiSelectStartTimeRef.current !== null) {
      const capturedFieldId = multiSelectFieldIdRef.current;
      const capturedStartTime = multiSelectStartTimeRef.current;

      const endTime = getDurationNow();
      const duration = Math.floor((endTime - capturedStartTime) / 1000);

      if (duration > 0) {
        setFieldTimeLogs(prev => [...prev, {
          fieldId: capturedFieldId,
          endTimestamp: endTime,
          duration,
          isSummary: false,
        }]);
        console.log(`[endMultiSelect] ${capturedFieldId}: start=${new Date(capturedStartTime).toISOString()}, end=${new Date(endTime).toISOString()}, duration=${duration}s`);
      }

      nextFieldStartTimeRef.current = endTime;
      multiSelectFieldIdRef.current = null;
      multiSelectStartTimeRef.current = null;
    }
  };

  const loadForm = async () => {
    setIsLoadingData(true);
    isFormReadyRef.current = false;
    setIsFormReady(false);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setStartLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => console.log("[TaskFormPage] Location init error:", err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }

    try {
      let res;
      if (isDemoMode) {
        if (!formCode) { alert("Invalid form code"); return; }
        res = await taskService.taskWebFormTry(formCode);
      } else {
        if (!sId) { alert("Invalid session ID"); return; }
        res = await taskService.taskForm(sId);
      }

      setSessionData({
        startDate: res.data?.startDate ?? "",
        sessionId: res.data?.sessionId ?? "",
      });

      if (res.data?.startDate) {
        const startDateObj = new Date(res.data.startDate.replace(" ", "T"));
        const serverStartTimestamp = startDateObj.getTime();

        serverStartTimestampRef.current = serverStartTimestamp;
        deviceTimeAtLoadRef.current = Date.now();
        nextFieldStartTimeRef.current = serverStartTimestamp;

        setFieldTimeLogs([]);
        isFormReadyRef.current = true;
        setIsFormReady(true);

        console.log(`[loadForm] Form ready. nextFieldStartTime = ${new Date(nextFieldStartTimeRef.current).toISOString()}`);
      }

      setFormData(res.data);
      checkAndRestoreDraft(res.data);
    } catch (e: any) {
      alert("Gagal memuat form");
    } finally {
      setIsLoadingData(false);
    }
  };

  const loadMoreAccessForm = async () => {
    setIsLoadingData(true);
    isFormReadyRef.current = false;
    setIsFormReady(false);
    try {
      const res = await taskService.taskFormByMAccess(tId!, moreAccessCoords!.lat, moreAccessCoords!.lng);

      setMoreAccessTaskId(res.data?.id ?? null);
      setSessionData({ startDate: res.data?.startDate ?? "", sessionId: res.data?.sessionId ?? "" });
      if (typeof res.data?.lockSubmitDistance === "boolean") {
        setLockSubmitDistance(res.data.lockSubmitDistance);
      }

      if (res.data?.startDate) {
        const startDateObj = new Date(res.data.startDate.replace(" ", "T"));
        const serverStartTimestamp = startDateObj.getTime();
        serverStartTimestampRef.current = serverStartTimestamp;
        deviceTimeAtLoadRef.current = Date.now();
        nextFieldStartTimeRef.current = serverStartTimestamp;
        setFieldTimeLogs([]);
        isFormReadyRef.current = true;
        setIsFormReady(true);
      }

      setFormData(res.data);
      checkAndRestoreDraft(res.data);
    } catch (e: any) {
      alert(typeof e === "string" ? e : "Gagal memuat form");
    } finally {
      setIsLoadingData(false);
    }
  };

  const getDraftStorageKeys = (resData?: any): string[] => {
    const keys: string[] = [];
    const taskId = resData?.id || moreAccessTaskId;
    if (taskId) keys.push(`webform_draft_task_${taskId}`);
    if (tId) keys.push(`webform_draft_maccess_${tId}`);
    if (sId) keys.push(`webform_draft_session_${sId}`);
    if (formCode) keys.push(`webform_draft_demo_${formCode}`);
    return keys;
  };

  const openAppWithDraftState = () => {
    const taskId = formData?.id || moreAccessTaskId;
    if (!taskId) return;
    const hasValues = values && Object.keys(values).length > 0;
    const lat = startLocation?.lat || moreAccessCoords?.lat || '';
    const lng = startLocation?.lng || moreAccessCoords?.lng || '';
    const deepLinkUrl = `moreaccess://task-detail?taskId=${taskId}&hasDraft=${hasValues ? 'true' : 'false'}&lat=${lat}&lng=${lng}`;
    console.log('[WebForm] Navigating to Deep Link:', deepLinkUrl);
    window.location.href = deepLinkUrl;
  };

  const clearDraftParam = searchParams.get("clearDraft") === "true";

  const checkAndRestoreDraft = (resData: any) => {
    if (!resData) return;
    const keys = getDraftStorageKeys(resData);
    if (!keys.length) return;

    if (clearDraftParam) {
      console.log("[WebFormDraft] clearDraft=true parameter found in URL. Purging local storage draft keys.");
      keys.forEach(k => {
        try {
          localStorage.removeItem(k);
        } catch (e) {
          console.error(`[WebFormDraft] Error removing draft key '${k}':`, e);
        }
      });
      return;
    }

    for (const draftKey of keys) {
      try {
        const raw = localStorage.getItem(draftKey);
        if (!raw) continue;

        const draft = JSON.parse(raw);
        if (!draft || typeof draft !== "object") continue;

        const serverVersion = resData.version ?? 1;
        const localVersion = draft.version ?? 1;
        const serverFormId = resData.formId || resData.id;

        if (localVersion === serverVersion && (!draft.formId || draft.formId === serverFormId)) {
          console.log(`[WebFormDraft] Local draft version matches server version (${localVersion}) for key '${draftKey}'. Restoring draft.`);
          if (draft.values && Object.keys(draft.values).length > 0) {
            setValues(draft.values);
          }
          if (draft.visitedPages && Array.isArray(draft.visitedPages)) {
            setVisitedPages(draft.visitedPages);
          }
          if (draft.fieldTimeLogs && Array.isArray(draft.fieldTimeLogs)) {
            setFieldTimeLogs(draft.fieldTimeLogs);
          }
          if (draft.photoMethods) {
            setPhotoMethods(draft.photoMethods);
          }
          if (draft.startLocation) {
            setStartLocation(draft.startLocation);
          }
          return;
        } else {
          console.log(`[WebFormDraft] Server version (${serverVersion}) differs from local draft version (${localVersion}) for key '${draftKey}'. Clearing draft.`);
          localStorage.removeItem(draftKey);
          alert("Terdapat versi form baru di server. Session form baru telah dibuat.");
        }
      } catch (err) {
        console.error(`[WebFormDraft] Error restoring draft for key '${draftKey}':`, err);
      }
    }
  };

  useEffect(() => {
    const restored = location.state as any;

    if (restored?.restoredValues) {
      console.log("[TaskFormPage] Restoring state from SummaryPage back navigation");

      setFormData(restored.restoredFormData);
      setSessionData(restored.restoredSessionData ?? { startDate: "", sessionId: "" });
      setValues(restored.restoredValues);
      setCurrentPageId(restored.restoredCurrentPageId ?? null);
      setPageStack(restored.restoredPageStack ?? []);
      setVisitedPages(restored.restoredVisitedPages ?? []);
      setVisitedFields(new Set(restored.restoredVisitedFields ?? []));

      if (restored.restoredFieldTimeLogs) {
        setFieldTimeLogs(restored.restoredFieldTimeLogs);
      }

      if (restored.restoredPhotoMethods) {
        setPhotoMethods(restored.restoredPhotoMethods);
      }

      if (restored.restoredServerStartTimestamp) {
        serverStartTimestampRef.current = restored.restoredServerStartTimestamp;
        deviceTimeAtLoadRef.current = restored.restoredDeviceTimeAtLoad ?? Date.now();
        nextFieldStartTimeRef.current = Date.now();
      }

      isFormReadyRef.current = true;
      setIsFormReady(true);

      if (restored.restoredMoreAccessTId) {
        setMoreAccessTaskId(restored.restoredMoreAccessTaskId ?? null);
        setMoreAccessCoords(restored.restoredMoreAccessCoords ?? null);
        setLocationStatus("granted");
      }
      return;
    }

    if (isMoreAccessMode) {
      setIsLoadingData(true);
      return;
    }

    loadForm();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (pageStack.length === 0) {
        console.log("[TaskFormPage] Browser/hardware back detected on first page. Purging local draft storage.");
        const keys = getDraftStorageKeys(formData);
        keys.forEach(k => {
          try { localStorage.removeItem(k); } catch (e) { }
        });
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [pageStack, formData]);

  useEffect(() => {
    if (!isMoreAccessMode || moreAccessCoords) return;
    setLocationStatus("requesting");
    if (!navigator.geolocation) { setLocationStatus("denied"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMoreAccessCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationStatus("granted");
      },
      (err) => { console.error(err); setLocationStatus("denied"); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [isMoreAccessMode]);

  useEffect(() => {
    if (!isMoreAccessMode || !clearDraftParam) return;
    const earlyKeys: string[] = [];
    if (tId) earlyKeys.push(`webform_draft_maccess_${tId}`);
    if (sId) earlyKeys.push(`webform_draft_session_${sId}`);
    if (moreAccessTaskId) earlyKeys.push(`webform_draft_task_${moreAccessTaskId}`);

    if (earlyKeys.length > 0) {
      console.log("[WebFormDraft] Early purge before API load. Clearing keys:", earlyKeys);
      earlyKeys.forEach(k => {
        try { localStorage.removeItem(k); } catch (e) { /* noop */ }
      });
    }

    const allKeys = Object.keys(localStorage);
    allKeys.forEach(k => {
      if (k.startsWith("webform_draft_task_") || k.startsWith("webform_draft_maccess_")) {
        const storedRaw = localStorage.getItem(k);
        if (!storedRaw) return;
        try {
          const stored = JSON.parse(storedRaw);
          if (tId && stored?.tId === tId) {
            localStorage.removeItem(k);
          }
        } catch { /* noop */ }
      }
    });
  }, [isMoreAccessMode, clearDraftParam, tId, sId, moreAccessTaskId]);

  useEffect(() => {
    if (!isMoreAccessMode || !moreAccessCoords || formData) return;
    loadMoreAccessForm();
  }, [isMoreAccessMode, moreAccessCoords]);

  useEffect(() => {
    if (formData?.pages && !currentPageId) {
      setCurrentPageId(formData.pages[0].id);
    }
  }, [formData]);

  const hasNotifiedFirstInputRef = useRef(false);

  useEffect(() => {
    if (!isFormReady || isDemoMode || !formData) return;
    const hasValues = values && Object.keys(values).length > 0;
    if (!hasValues) return;

    const keys = getDraftStorageKeys(formData);
    if (!keys.length) return;

    const serverVersion = formData.version ?? 1;
    const serverFormId = formData.formId || formData.id;

    const draftPayload = {
      version: serverVersion,
      formId: serverFormId,
      values,
      visitedPages,
      fieldTimeLogs,
      photoMethods,
      startLocation,
      updatedAt: new Date().toISOString(),
    };

    const payloadStr = JSON.stringify(draftPayload);
    keys.forEach(key => {
      try {
        localStorage.setItem(key, payloadStr);
      } catch (err) {
        console.error(`[WebFormDraft] Error saving draft for key '${key}':`, err);
      }
    });
  }, [values, visitedPages, fieldTimeLogs, photoMethods, startLocation, isFormReady, formData, isDemoMode]);

  useEffect(() => {
    if (currentPageId && formData?.pages) {
      setVisitedPages(prev => [...new Set([...prev, currentPageId])]);
      const current = formData.pages.find((p: any) => p.id === currentPageId);
      if (current?.fields) {
        setVisitedFields(prev => {
          const next = new Set(prev);
          current.fields.forEach((f: any) => next.add(f.id));
          return next;
        });
      }
    }
  }, [currentPageId, formData]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    if (!currentPageId || !isFormReady || isDemoMode) return;
    console.log(`[pageChange] Page changed to ${currentPageId}, next start time remains: ${nextFieldStartTimeRef.current ? new Date(nextFieldStartTimeRef.current).toISOString() : 'null'}`);
  }, [currentPageId, isFormReady]);

  useEffect(() => {
    if (!currentPage) return;

    let initialHidden = new Set<string>();
    let newDynamicMins: Record<string, number> = {};

    const pageRules = currentPage.actionRules || "";
    const [pagePredefine] = pageRules.includes("##")
      ? pageRules.split("##").map((s: string) => s.trim())
      : [null];

    if (pagePredefine && hasCondShow(pagePredefine)) {
      const allIds = currentPage.fields.map((f: any) => f.id);
      initialHidden = new Set(allIds);
    }

    const applyVisibilityRules = (ruleStr: string, isFieldLevel: boolean) => {
      const ruleLines = ruleStr.split(";").map((r: string) => r.trim()).filter(Boolean);
      for (const rule of ruleLines) {
        const ternaryMatch = rule.match(/^(.+?)\?\s*(.+)$/);
        if (ternaryMatch) {
          const [, conditionPart, actionPart] = ternaryMatch;
          const isConditionMet = evaluateCondition(conditionPart.trim(), values);
          if (!isConditionMet) {
            if (isFieldLevel) {
              const condShowMatch = actionPart.trim().match(/^cond_show\("([^"]+)"\)$/);
              if (condShowMatch) initialHidden.add(condShowMatch[1]);
            }
            continue;
          }
          applyVisibilityRules(actionPart.trim(), isFieldLevel);
          continue;
        }

        const hideMatch = rule.match(/^hide\("([^"]+)"\)$/);
        if (hideMatch) initialHidden.add(hideMatch[1]);

        const condShowMatch = rule.match(/^cond_show\("([^"]+)"\)$/);
        if (condShowMatch) initialHidden.delete(condShowMatch[1]);

        const minRefMatch = rule.match(/^field_min_value_by_reference\("([^"]+)",\s*"([^"]+)"\)$/);
        if (minRefMatch) {
          const refValue = parseInt(values[minRefMatch[2]], 10);
          newDynamicMins[minRefMatch[1]] = isNaN(refValue) ? 0 : refValue;
        }
      }
    };

    if (pagePredefine) applyVisibilityRules(pagePredefine, false);

    currentPage.fields.forEach((field: any) => {
      if (initialHidden.has(field.id)) return;
      if (field.actionRules) {
        const [, actionPart] = field.actionRules.includes("##")
          ? field.actionRules.split("##").map((s: string) => s.trim())
          : [null, field.actionRules];
        if (actionPart && (actionPart.includes("cond_show") || actionPart.includes("hide") || actionPart.includes("field_min_value_by_reference"))) {
          applyVisibilityRules(actionPart, true);
        }
      }
    });

    setHiddenFields(initialHidden);
    setDynamicMinValues(newDynamicMins);
  }, [currentPageId, values]);

  const hasCondShow = (rules: string) => /cond_show\(/.test(rules);

  const getAdditionalInfo = () => {
    const item = (location.state as any)?.item || formData?.items?.[0];
    return item?.form?.additionalInfo ?? item?.additionalInfo ?? formData?.additionalInfo;
  };

  const getAiValues = (key: string): string[] => {
    const info = getAdditionalInfo();
    if (info && info[key] != null) {
      const val = info[key];
      if (typeof val === "string") {
        return val.split(";").map((s: string) => s.trim()).filter(Boolean);
      }
      if (Array.isArray(val)) {
        return val.map((s: any) => String(s).trim()).filter(Boolean);
      }
      return [String(val).trim()];
    }

    if (formData?.pages) {
      const regex = new RegExp(`option_reference_by_info\\(\\s*(?:f\\.)?[^,)]+,?\\s*"${key}"\\s*\\)`);
      const fallbackValues: string[] = [];

      for (const p of formData.pages) {
        const pageRules = p.actionRules || "";
        for (const f of p.fields || []) {
          const fieldRules = f.actionRules || "";
          const combinedRules = `${pageRules};${fieldRules}`;

          if (regex.test(combinedRules)) {
            const nothingOptions = new Set<string>();
            const nothingRegex = /field_option_nothing\(\s*(?:f\.)?"?[^"]+"?,?\s*"([^"]+)"\s*\)/g;
            for (const match of combinedRules.matchAll(nothingRegex)) {
              if (match[1]) nothingOptions.add(match[1].trim().toLowerCase());
            }

            for (const opt of f.options || []) {
              if (opt === "enable_add_remove_value") continue;
              if (nothingOptions.has(opt.trim().toLowerCase())) continue;
              if (!fallbackValues.includes(opt)) fallbackValues.push(opt);
            }
          }
        }
      }
      if (fallbackValues.length > 0) return fallbackValues;
    }

    return [];
  };

  const isAiMatch = (key: string, expected: string): boolean => {
    const items = getAiValues(key);
    const cleanExp = expected.trim().toLowerCase();
    const expWords = cleanExp.split(/\s+/).filter(Boolean).sort().join(" ");

    for (const it of items) {
      const cleanIt = it.trim().toLowerCase();
      if (cleanIt === cleanExp) return true;
      const itWords = cleanIt.split(/\s+/).filter(Boolean).sort().join(" ");
      if (itWords === expWords) return true;
    }
    return false;
  };

  const getEffectiveFieldOptions = (field: any, page?: any): string[] => {
    const rawOptions: string[] = field?.options || [];
    if (rawOptions.length === 0) return [];
    if (isDemoMode) return rawOptions;

    const targetPage = page || currentPage;
    const pageRules = targetPage?.actionRules || "";
    const fieldRules = field?.actionRules || "";

    let infoKey: string | null = null;

    if (pageRules) {
      const pageRuleRegex = new RegExp(
        `option_reference_by_info\\(\\s*(?:f\\.)?"?${field.id}"?,?\\s*"([^"]+)"\\s*\\)`
      );
      const match = pageRules.match(pageRuleRegex);
      if (match) {
        infoKey = match[1];
      }
    }

    if (!infoKey && fieldRules) {
      const fieldRuleRegex = new RegExp(
        `option_reference_by_info\\(\\s*(?:f\\.)?"?${field.id}"?,?\\s*"([^"]+)"\\s*\\)`
      );
      const match = fieldRules.match(fieldRuleRegex);
      if (match) {
        infoKey = match[1];
      } else {
        const fieldOnlyRegex = /option_reference_by_info\(\s*"([^"]+)"\s*\)/;
        const matchOnly = fieldRules.match(fieldOnlyRegex);
        if (matchOnly) {
          infoKey = matchOnly[1];
        }
      }
    }

    if (!infoKey) {
      return rawOptions;
    }

    const additionalInfo = getAdditionalInfo();
    const rawInfoVal = additionalInfo ? additionalInfo[infoKey] : null;

    let allowedItems: string[] = [];
    if (typeof rawInfoVal === "string") {
      allowedItems = rawInfoVal
        .split(";")
        .map((s: string) => s.trim())
        .filter(Boolean);
    } else if (Array.isArray(rawInfoVal)) {
      allowedItems = rawInfoVal
        .map((s: any) => String(s).trim())
        .filter(Boolean);
    } else if (rawInfoVal != null && rawInfoVal !== "") {
      allowedItems = [String(rawInfoVal).trim()];
    }

    const nothingOptions = new Set<string>();
    const combinedRules = `${pageRules};${fieldRules}`;
    const nothingRegexWithField = /field_option_nothing\(\s*(?:f\.)?"[^"]+",\s*"([^"]+)"\s*\)/g;
    for (const match of combinedRules.matchAll(nothingRegexWithField)) {
      if (match[1]) {
        nothingOptions.add(match[1].trim().toLowerCase());
      }
    }
    const nothingRegexSingle = /field_option_nothing\(\s*"([^"]+)"\s*\)/g;
    for (const match of fieldRules.matchAll(nothingRegexSingle)) {
      if (match[1]) {
        nothingOptions.add(match[1].trim().toLowerCase());
      }
    }

    const isMatchedWithInfo = (opt: string): boolean => {
      const cleanOpt = opt.trim().toLowerCase();
      const optWords = cleanOpt.split(/\s+/).filter(Boolean).sort().join(" ");

      for (const itemVal of allowedItems) {
        const cleanItem = itemVal.trim().toLowerCase();
        if (cleanOpt === cleanItem) return true;
        const itemWords = cleanItem.split(/\s+/).filter(Boolean).sort().join(" ");
        if (optWords === itemWords) return true;
      }
      return false;
    };

    return rawOptions.filter((opt: string) => {
      if (opt === "enable_add_remove_value") return true;
      if (nothingOptions.has(opt.trim().toLowerCase())) return true;
      return isMatchedWithInfo(opt);
    });
  };

  const evaluateSimpleCondition = (condition: string, vals: Record<string, any>): boolean => {
    const orParts = condition.split("||").map(p => p.trim());
    return orParts.some(orPart => {
      const andParts = orPart.split("&&").map(p => p.trim());
      return andParts.every(part => {
        const t = part.trim();
        if (t === "TRUE") return true;
        if (t === "FALSE") return false;
        let match;

        match = t.match(/^ai\."([^"]+)"\s*==\s*"([^"]+)"$/);
        if (match) {
          const [, infoKey, expected] = match;
          const info = getAdditionalInfo();
          if (info && info[infoKey] != null) {
            const directVal = String(info[infoKey]).trim().toLowerCase();
            if (directVal === expected.trim().toLowerCase()) return true;
          }
          return isAiMatch(infoKey, expected);
        }

        match = t.match(/^ai\."([^"]+)"\s*!=\s*"([^"]+)"$/);
        if (match) {
          const [, infoKey, expected] = match;
          const info = getAdditionalInfo();
          if (info && info[infoKey] != null) {
            const directVal = String(info[infoKey]).trim().toLowerCase();
            if (directVal === expected.trim().toLowerCase()) return false;
          }
          return !isAiMatch(infoKey, expected);
        }

        match = t.match(/^ai\."([^"]+)"\s*(?:=\(\)\=?|=\{\}\=?)\s*(?:\{"([^"]+)"\}|"([^"]+)")$/);
        if (match) {
          const [, infoKey, expWithBraces, expWithoutBraces] = match;
          const expected = expWithBraces || expWithoutBraces;
          return isAiMatch(infoKey, expected);
        }

        match = t.match(/^ai\."([^"]+)"\s*(?:!\=\(\)\=?|!\=\{\}\=?)\s*(?:\{"([^"]+)"\}|"([^"]+)")$/);
        if (match) {
          const [, infoKey, expWithBraces, expWithoutBraces] = match;
          const expected = expWithBraces || expWithoutBraces;
          return !isAiMatch(infoKey, expected);
        }

        match = t.match(/^f\."([^"]+)"\s*==\s*"([^"]+)"$/);
        if (match) return String(vals[match[1]]) === match[2];

        match = t.match(/^f\."([^"]+)"\s*!=\s*"([^"]+)"$/);
        if (match) {
          const v = vals[match[1]];
          if (v === undefined || v === null || v === "") return false;
          return Array.isArray(v) ? !v.includes(match[2]) : String(v) !== match[2];
        }

        match = t.match(/^f\."([^"]+)"\s*(?:=\(\)\=?|=\{\}\=?)\s*\{"([^"]+)"\}$/);
        if (match) { const v = vals[match[1]]; return Array.isArray(v) ? v.includes(match[2]) : v === match[2]; }

        match = t.match(/^f\."([^"]+)"\s*!\=\(\)\=?\s*\{"([^"]+)"\}$/);
        if (match) { const v = vals[match[1]]; return Array.isArray(v) && !v.includes(match[2]); }

        match = t.match(/^f\."([^"]+)"\s*(=\(\)=|<\(\)=|<\(\)<|>\(\)=|>\(\)>)\s*"([^"]+)"$/);
        if (match) {
          const v = vals[match[1]];
          if (v === undefined || v === null || v === "") return false;
          const valNum = Number(v), targetNum = Number(match[3]);
          if (!isNaN(valNum) && !isNaN(targetNum)) {
            switch (match[2]) {
              case "=()=": return valNum === targetNum;
              case "<()=": return valNum <= targetNum;
              case "<()<": return valNum < targetNum;
              case ">()=": return valNum >= targetNum;
              case ">()>": return valNum > targetNum;
            }
          }
          return false;
        }

        match = t.match(/^f\."([^"]+)"\s*(=\(\)=|<\(\)=|<\(\)<|>\(\)=|>\(\)>)\s*\{(\d+)\}$/);
        if (match) {
          const targetLen = parseInt(match[3], 10);
          const v = vals[match[1]];
          let len = 0;
          if (Array.isArray(v)) len = v.length;
          else if (v && typeof v === "string") len = v.trim().length > 0 ? 1 : 0;
          else if (v) len = 1;
          switch (match[2]) {
            case "=()=": return len === targetLen;
            case "<()=": return len <= targetLen;
            case "<()<": return len < targetLen;
            case ">()=": return len >= targetLen;
            case ">()>": return len > targetLen;
          }
          return false;
        }

        return false;
      });
    });
  };

  const evaluateCondition = (condition: string, vals: Record<string, any>): boolean => {
    const stringMap: Record<string, string> = {};
    let maskIdx = 0;
    let masked = condition.replace(/(?:\{"[^"]+"\})|(?:"[^"]+")/g, match => {
      const key = `__STR_${maskIdx++}__`;
      stringMap[key] = match;
      return key;
    });
    masked = masked.replace(/\(\)/g, "__OP__");
    const unmask = (str: string) => {
      let res = str.replace(/__OP__/g, "()");
      for (const [key, val] of Object.entries(stringMap)) res = res.replace(key, val);
      return res;
    };
    while (/\([^()]+\)/.test(masked)) {
      masked = masked.replace(/\(([^()]+)\)/g, (_, inner) =>
        evaluateSimpleCondition(unmask(inner), vals) ? "TRUE" : "FALSE"
      );
    }
    return evaluateSimpleCondition(unmask(masked), vals);
  };

  const computeHiddenFieldsForPage = (page: any, vals: Record<string, any>): Set<string> => {
    let hidden = new Set<string>();

    const pageRules = page.actionRules || "";
    const [pagePredefine] = pageRules.includes("##")
      ? pageRules.split("##").map((s: string) => s.trim())
      : [null];

    if (pagePredefine && hasCondShow(pagePredefine)) {
      hidden = new Set(page.fields.map((f: any) => f.id));
    }

    const applyVisibilityRules = (ruleStr: string, isFieldLevel: boolean) => {
      const ruleLines = ruleStr.split(";").map((r: string) => r.trim()).filter(Boolean);
      for (const rule of ruleLines) {
        const ternaryMatch = rule.match(/^(.+?)\?\s*(.+)$/);
        if (ternaryMatch) {
          const [, conditionPart, actionPart] = ternaryMatch;
          const isConditionMet = evaluateCondition(conditionPart.trim(), vals);
          if (!isConditionMet) {
            if (isFieldLevel) {
              const condShowMatch = actionPart.trim().match(/^cond_show\("([^"]+)"\)$/);
              if (condShowMatch) hidden.add(condShowMatch[1]);
            }
            continue;
          }
          applyVisibilityRules(actionPart.trim(), isFieldLevel);
          continue;
        }

        const hideMatch = rule.match(/^hide\("([^"]+)"\)$/);
        if (hideMatch) hidden.add(hideMatch[1]);

        const condShowMatch = rule.match(/^cond_show\("([^"]+)"\)$/);
        if (condShowMatch) hidden.delete(condShowMatch[1]);
      }
    };

    if (pagePredefine) applyVisibilityRules(pagePredefine, false);

    page.fields.forEach((field: any) => {
      if (hidden.has(field.id)) return;
      if (field.actionRules) {
        const [, actionPart] = field.actionRules.includes("##")
          ? field.actionRules.split("##").map((s: string) => s.trim())
          : [null, field.actionRules];
        if (actionPart && (actionPart.includes("cond_show") || actionPart.includes("hide"))) {
          applyVisibilityRules(actionPart, true);
        }
      }
    });

    return hidden;
  };

  const validateCurrentPage = (): boolean => {
    if (!currentPage) return true;

    const visibleFields = (currentPage.fields || []).filter((f: any) => !hiddenFields.has(f.id));

    const emptyFields = visibleFields.filter((field: any) => {
      const isMatrix = field.type?.value === "Matrix Reference" || (field.type as any)?.key === 7;
      if (isMatrix) return false;
      const value = values[field.id];
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "string") return value.trim() === "";
      return value == null || value === "";
    });

    if (emptyFields.length > 0) {
      alert("Semua pertanyaan pada halaman ini wajib diisi.");
      return false;
    }

    for (const field of visibleFields) {
      const isMatrix = field.type?.value === "Matrix Reference" || (field.type as any)?.key === 7;
      if (isMatrix) {
        const sourceField = findSourceField(field.id);
        if (sourceField) {
          const sourceValue = values[sourceField.id];
          const selectedOptions = Array.isArray(sourceValue) ? sourceValue : (sourceValue ? [sourceValue] : []);
          if (selectedOptions.length > 0) {
            const matrixValues = values[field.id] || {};
            for (const optPattern of field.options || []) {
              const match = optPattern.match(/^(.*)\s*\[(\d+)\]$/);
              if (match) {
                const labelTemplate = match[1].trim();
                for (const opt of selectedOptions) {
                  const subLabel = labelTemplate.replace("$option", opt);
                  const subVal = matrixValues[subLabel];
                  const isEmpty = Array.isArray(subVal) ? subVal.length === 0 : !subVal;
                  if (isEmpty) {
                    alert(`Semua pertanyaan pada "${field.label || "Matrix Reference"}" wajib diisi.`);
                    return false;
                  }
                }
              }
            }
          }
        }
      }

      let exactRequired = 0;
      const minRefRegex = new RegExp(`field_min_value_by_reference\\("${field.id}",\\s*"([^"]+)"\\)`);
      if (currentPage.actionRules) {
        const match = currentPage.actionRules.match(minRefRegex);
        if (match) {
          const refRaw = values[match[1]];
          const refStr = Array.isArray(refRaw) ? refRaw[0] : refRaw;
          const parsed = parseInt(refStr, 10);
          if (!isNaN(parsed)) exactRequired = parsed;
        }
      }
      const isTakePicture = field.type?.value === "Take Picture" || field.type?.key === 3;
      if (isTakePicture) exactRequired = exactRequired || 1;
      if (exactRequired > 0 && !isMatrix) {
        const value = values[field.id];
        const len = Array.isArray(value) ? value.length : (value ? 1 : 0);
        if (len !== exactRequired) {
          alert(`Harap masukkan tepat ${exactRequired} foto untuk "${field.label}".`);
          return false;
        }
      }
    }

    return true;
  };

  const getPagePrimaryAction = (page: any) => {
    if (!page?.actionRules) return "submit";
    const rules = page.actionRules.split(";").map((r: string) => r.trim());
    const hasOpenPage = rules.some((r: string) => r.includes("open_page"));
    const hasSubmit = rules.some((r: string) => r === "submit" || r.endsWith("? submit"));
    if (hasOpenPage) return "next";
    if (hasSubmit) return "submit";
    return "submit";
  };

  const pagePrimaryAction = getPagePrimaryAction(currentPage);

  const handleChange = (fieldId: string, value: any, field?: any) => {
    setValues(prev => {
      const updated = { ...prev, [fieldId]: value };
      if (field?.actionRules) handleActionRules(field.actionRules, updated, "field");
      return updated;
    });
  };

  const handlePicturePress = (
    fieldId: string,
    methodCamera: boolean,
    methodGallery: boolean,
    availableRatioCameraInApps?: string
  ) => {
    setSelectedFieldRatio(availableRatioCameraInApps);
    if (methodCamera && methodGallery) {
      setMethodFieldId(fieldId);
      setShowMethodModal(true);
    } else if (methodCamera) {
      openCamera(fieldId, availableRatioCameraInApps);
    } else if (methodGallery) {
      openGallery(fieldId);
    }
  };

  const openGallery = (fieldId: string) => {
    setActiveCameraField(fieldId);
    galleryInputRef.current?.click();
  };

  const handleGalleryFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeCameraField) return;

    setPreviewBlob(file);
    setPreviewUrl(URL.createObjectURL(file));
    setTempImageSource('storage');
    setShowPreviewModal(true);

    e.target.value = "";
  };

  const handleCheckboxChange = (fieldId: string, option: string, actionRules: any) => {
    setValues(prev => {
      const current = prev[fieldId] || [];
      let updated: string[] = [];

      const ruleMatch = actionRules?.match(/field_option_nothing\(f\."([^"]+)",\s*"([^"]+)"\)/);
      if (ruleMatch) {
        const exclusiveOption = ruleMatch[2];
        if (option === exclusiveOption) {
          updated = current.includes(option) ? [] : [option];
        } else {
          updated = current.includes(option)
            ? current.filter((o: string) => o !== option)
            : [...current.filter((o: string) => o !== exclusiveOption), option];
        }
      } else {
        updated = current.includes(option)
          ? current.filter((o: string) => o !== option)
          : [...current, option];
      }

      const nextValues = { ...prev, [fieldId]: updated };
      if (actionRules) {
        const otherRules = actionRules
          .replace(/field_option_nothing\(.*?\);?/g, "")
          .replace(/option_reference_by_info\(.*?\);?/g, "")
          .trim();
        if (otherRules.length > 0) handleActionRules(otherRules, nextValues, "field");
      }
      return nextValues;
    });
  };

  const handleActionRules = (rules: string, updatedValues: Record<string, any>, scope: "field" | "page" = "field") => {
    if (!rules) return;
    const [, actionPart] = rules.includes("##")
      ? rules.split("##").map(s => s.trim())
      : [null, rules];
    if (actionPart) runRuleLines(actionPart, updatedValues);
  };

  const runRuleLines = (rules: string, vals: Record<string, any>) => {
    const ruleLines = rules.split(";").map(r => r.trim()).filter(Boolean);
    for (const rule of ruleLines) {
      const ternaryMatch = rule.match(/^(.+?)\?\s*(.+)$/);
      if (ternaryMatch) {
        const [, conditionPart, actionPart] = ternaryMatch;
        if (!evaluateCondition(conditionPart.trim(), vals)) continue;
        runRuleLines(actionPart.trim(), vals);
        return;
      }

      const openPageMatch = rule.match(/^open_page\("([^"]+)"\)$/);
      if (openPageMatch) {
        if (!validateCurrentPage()) return;
        endMultiSelectAndLog();
        if (currentActiveFieldIdRef.current) {
          endFieldAndLog(currentActiveFieldIdRef.current);
          currentActiveFieldIdRef.current = null;
        }
        setSlideDirection("left");
        setPageStack(stack => [...stack, currentPageId!]);
        setCurrentPageId(openPageMatch[1]);
        return;
      }

      const endMatch = rule.match(/^end\("([^"]+)"\)$/);
      if (endMatch) {
        window.alert(endMatch[1]);
        navigate(-1);
        return;
      }

      if (rule === "submit") {
        if (!validateCurrentPage()) return;
        endMultiSelectAndLog();
        if (currentActiveFieldIdRef.current) {
          endFieldAndLog(currentActiveFieldIdRef.current);
          currentActiveFieldIdRef.current = null;
        }
        if (isDemoMode) navigateToSummary();
        else checkAndNavigateToSummary();
        return;
      }
    }
  };

  const checkAndNavigateToSummary = () => {
    const page = formData?.pages?.find((p: any) => p.id === currentPageId);
    const visibleFields = (page?.fields || []).filter((f: any) => !hiddenFields.has(f.id));
    const emptyFields = visibleFields.filter((field: any) => {
      const value = values[field.id];
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "string") return value.trim() === "";
      return value == null || value === "";
    });
    if (emptyFields.length > 0) {
      alert("Ada field yang belum diisi");
      return;
    }
    navigateToSummary();
  };

  const navigateToSummary = () => {
    endMultiSelectAndLog();
    if (currentActiveFieldIdRef.current) {
      endFieldAndLog(currentActiveFieldIdRef.current);
      currentActiveFieldIdRef.current = null;
    }

    const summaryStartTime = nextFieldStartTimeRef.current ?? getDurationNow();

    const filteredValues = { ...values };
    formData?.pages?.forEach((page: any) => {
      if (!visitedPages.includes(page.id)) return;
      const hiddenForThisPage = computeHiddenFieldsForPage(page, values);
      page.fields.forEach((field: any) => {
        if (hiddenForThisPage.has(field.id)) {
          delete filteredValues[field.id];
        }
      });
    });

    navigate(
      isMoreAccessMode
        ? "/mAccess/summary"
        : isDemoMode
          ? "/summary?demo=true"
          : "/summary",
      {
        state: {
          sId,
          sessionData,
          formData,
          kmlData: formData?.kmlData,
          values: filteredValues,
          visitedPages,
          visitedFields: Array.from(visitedFields),
          isDemoMode,
          fieldTimeLogs: isDemoMode ? [] : fieldTimeLogs,
          photoMethods,
          summaryStartTimestamp: summaryStartTime,
          isMoreAccessMode,
          moreAccessTId: tId,
          moreAccessTaskId,
          moreAccessCoords,
          startLocation: startLocation || moreAccessCoords,
          lockSubmitDistance,
          restoredValues: values,
          restoredCurrentPageId: currentPageId,
          restoredPageStack: pageStack,
          restoredVisitedPages: visitedPages,
          restoredVisitedFields: Array.from(visitedFields),
          restoredFormData: formData,
          restoredSessionData: sessionData,
          restoredFieldTimeLogs: isDemoMode ? [] : fieldTimeLogs,
          restoredServerStartTimestamp: serverStartTimestampRef.current,
          restoredDeviceTimeAtLoad: deviceTimeAtLoadRef.current,
          restoredMoreAccessTId: tId,
          restoredMoreAccessTaskId: moreAccessTaskId,
          restoredMoreAccessCoords: moreAccessCoords,
          restoredPhotoMethods: photoMethods,
          consentCode: consentCodeParam,
          consentValue: consentValueParam,
          consentAttachmentUrl: consentAttachmentUrlParam,
        },
      });
  };

  const handleBackPage = () => {
    const hasData = currentPage?.fields?.some((field: any) => {
      const v = values[field.id];
      return v && (!Array.isArray(v) || v.length > 0);
    });

    if (hasData) {
      if (!window.confirm("Data pada halaman ini belum dikirim. Yakin ingin kembali?")) return;
    }

    endMultiSelectAndLog();
    if (currentActiveFieldIdRef.current) {
      endFieldAndLog(currentActiveFieldIdRef.current);
      currentActiveFieldIdRef.current = null;
    }

    if (pageStack.length > 0) {
      const prev = pageStack[pageStack.length - 1];

      const leavingPage = formData?.pages?.find((p: any) => p.id === currentPageId);
      if (leavingPage) {
        setValues(v => {
          const updated = { ...v };
          leavingPage.fields.forEach((f: any) => delete updated[f.id]);
          return updated;
        });
        setVisitedPages(pages => pages.filter(pid => pid !== currentPageId));
        setVisitedFields(fields => {
          const updated = new Set(fields);
          leavingPage.fields.forEach((f: any) => updated.delete(f.id));
          return updated;
        });
      }

      setPageStack(stack => stack.slice(0, -1));

      const prevPage = formData?.pages?.find((p: any) => p.id === prev);
      if (prevPage?.fields) {
        setVisitedFields(fields => {
          const updated = new Set(fields);
          prevPage.fields.forEach((f: any) => updated.add(f.id));
          return updated;
        });
      }

      setSlideDirection("right");
      setCurrentPageId(prev);
    } else {
      const keys = getDraftStorageKeys();
      keys.forEach(k => {
        try { localStorage.removeItem(k); } catch (e) { }
      });
      navigate(-1);
    }
  };

  const openCamera = async (fieldId: string, availableRatioCameraInApps?: string) => {
    const ratioToUse = availableRatioCameraInApps ?? selectedFieldRatio;
    setSelectedFieldRatio(ratioToUse);
    const allowedKeys = parseAllowedAspectRatios(ratioToUse);
    if (allowedKeys.length > 0 && !allowedKeys.includes(aspectRatioKey)) {
      setAspectRatioKey(allowedKeys[0]);
    }
    if (!isDemoMode && isFormReadyRef.current) {
      currentActiveFieldIdRef.current = fieldId;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1920 } },
        audio: false,
      });
      setCameraStream(stream);
      setActiveCameraField(fieldId);
      setShowCameraModal(true);
    } catch {
      alert("Camera tidak tersedia");
    }
  };

  const switchCameraFacing = async () => {
    if (!activeCameraField) return;
    stopCamera();
    const nextFacing = facingMode === "environment" ? "user" : "environment";
    setFacingMode(nextFacing);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: nextFacing, width: { ideal: 1920 }, height: { ideal: 1920 } },
        audio: false,
      });
      setCameraStream(stream);
    } catch {
      alert("Gagal beralih kamera");
      setFacingMode(facingMode);
    }
  };

  const capturePhoto = () => {
    const video = document.getElementById("camera-video") as HTMLVideoElement;
    if (!video) return;

    const targetRatio = ASPECT_RATIOS.find(r => r.key === aspectRatioKey)!.ratio;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const currentRatio = vw / vh;

    let cropWidth = vw;
    let cropHeight = vh;

    if (currentRatio > targetRatio) {
      cropWidth = vh * targetRatio;
    } else {
      cropHeight = vw / targetRatio;
    }

    const sx = (vw - cropWidth) / 2;
    const sy = (vh - cropHeight) / 2;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(cropWidth);
    canvas.height = Math.round(cropHeight);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, sx, sy, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(blob => {
      if (!blob) return;
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setTempImageSource('in_apps_camera');
      stopCamera();
      setShowCameraModal(false);
      setShowPreviewModal(true);
    }, "image/jpeg", 1.0);
  };

  const uploadCapturedPhoto = async () => {
    if (!previewBlob || !activeCameraField) return;
    try {
      setUploadingPhoto(true);

      const isMatrixSub = activeCameraField.includes('_matrixsub_');
      let finalFieldId = activeCameraField;
      let matrixSubKey = '';
      if (isMatrixSub) {
        const parts = activeCameraField.split('_matrixsub_');
        finalFieldId = parts[0];
        matrixSubKey = parts[1];
      }

      const rawExistingValue = isMatrixSub
        ? (values[finalFieldId]?.[matrixSubKey] || [])
        : (values[finalFieldId] || []);

      const currentPhotos = Array.isArray(rawExistingValue)
        ? rawExistingValue
        : rawExistingValue ? [rawExistingValue] : [];

      const methodToRecord: 'in_apps_camera' | 'storage' = tempImageSource ?? 'in_apps_camera';

      if (isDemoMode) {
        if (isMatrixSub) {
          setValues(v => ({
            ...v,
            [finalFieldId]: {
              ...(v[finalFieldId] || {}),
              [matrixSubKey]: [...currentPhotos, previewUrl]
            }
          }));
        } else {
          handleChange(finalFieldId, [...currentPhotos, previewUrl]);
        }

        const photoMethodKey = isMatrixSub ? `${finalFieldId}_${matrixSubKey}` : finalFieldId;
        setPhotoMethods(prev => ({
          ...prev,
          [photoMethodKey]: [...(prev[photoMethodKey] || []), methodToRecord],
        }));

        setShowPreviewModal(false);
        setPreviewBlob(null);
        setPreviewUrl(null);
        setTempImageSource(null);
        return;
      }

      const file = new File([previewBlob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });

      let uploadedUrl: string | undefined;
      if (isMoreAccessMode) {
        if (!tId || !moreAccessTaskId) { alert("Sesi tidak valid"); return; }
        const res = await taskService.taskUploadDocumentByMAccess(tId, moreAccessTaskId, finalFieldId, file);
        uploadedUrl = res.data?.url;
      } else {
        if (!sId) { alert("Invalid session ID"); return; }
        const res = await taskService.taskUploadDocument(sId, finalFieldId, file);
        uploadedUrl = res.data?.url;
      }

      if (isMatrixSub) {
        setValues(v => ({
          ...v,
          [finalFieldId]: {
            ...(v[finalFieldId] || {}),
            [matrixSubKey]: [...currentPhotos, uploadedUrl]
          }
        }));
      } else {
        handleChange(finalFieldId, [...currentPhotos, uploadedUrl]);
      }

      const photoMethodKey = isMatrixSub ? `${finalFieldId}_${matrixSubKey}` : finalFieldId;
      setPhotoMethods(prev => ({
        ...prev,
        [photoMethodKey]: [...(prev[photoMethodKey] || []), methodToRecord],
      }));

      endFieldAndLog(finalFieldId);
      if (currentActiveFieldIdRef.current === activeCameraField || currentActiveFieldIdRef.current === finalFieldId) {
        currentActiveFieldIdRef.current = null;
      }

      setShowPreviewModal(false);
      setPreviewBlob(null);
      setPreviewUrl(null);
      setTempImageSource(null);
    } catch {
      alert("Upload gagal");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const retakePhoto = () => {
    setPreviewBlob(null);
    setPreviewUrl(null);
    setShowPreviewModal(false);

    if (!activeCameraField) return;

    const fieldIdToRetake = activeCameraField;
    const field = formData?.pages
      ?.flatMap((p: any) => p.fields)
      ?.find((f: any) => f.id === fieldIdToRetake);

    if (field) {
      handlePicturePress(fieldIdToRetake, field.methodCamera, field.methodGallery, field.availableRatioCameraInApps);
    } else {
      openCamera(fieldIdToRetake);
    }
  };

  const stopCamera = () => {
    cameraStream?.getTracks().forEach(track => track.stop());
    setCameraStream(null);
  };

  const renderDropdownSelectionModal = () => {
    if (!dropdownModalData) return null;
    const { field, displayOptions, mergedCustomOptions, isAddRemoveEnabled, isMultiple } = dropdownModalData;
    const fieldId = field.id;
    const currentVal = values[fieldId];

    return (
      <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.6)", zIndex: 1060 }}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <div>
                <h6 className="modal-title fw-bold mb-0">{field.label}</h6>
                <small className="text-muted">
                  {isMultiple ? "Pilih satu atau lebih opsi" : "Pilih salah satu opsi"}
                </small>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setDropdownModalData(null)}
              />
            </div>
            <div className="modal-body p-3" style={{ maxHeight: "360px", overflowY: "auto" }}>
              <div className="d-flex flex-column gap-2">
                {displayOptions.map((opt: string, i: number) => {
                  const isSelected = isMultiple
                    ? Array.isArray(currentVal) && currentVal.includes(opt)
                    : currentVal === opt;
                  const isCustomOption = mergedCustomOptions.includes(opt);

                  return (
                    <div key={i} className="d-flex align-items-center gap-2">
                      <div
                        className="p-2 flex-grow-1 d-flex align-items-center gap-2"
                        style={{
                          border: isSelected ? "1.5px solid #0d6efd" : "1px solid #dee2e6",
                          borderRadius: "6px",
                          backgroundColor: isSelected ? "#e7f1ff" : "#fff",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (isMultiple) {
                            handleCheckboxChange(fieldId, opt, field.actionRules);
                          } else {
                            if (!isDemoMode) endFieldAndLog(fieldId);
                            handleChange(fieldId, opt, field);
                            setDropdownModalData(null);
                          }
                        }}
                      >
                        <input
                          className="form-check-input m-0"
                          type={isMultiple ? "checkbox" : "radio"}
                          checked={isSelected}
                          readOnly
                        />
                        <span className="small fw-medium">{opt}</span>
                      </div>
                      {isCustomOption && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => {
                            if (isMultiple) {
                              if (isSelected) handleCheckboxChange(fieldId, opt, field.actionRules);
                            } else {
                              if (isSelected) {
                                handleChange(fieldId, "", field);
                              }
                            }
                            setCustomOptions(prev => ({
                              ...prev,
                              [fieldId]: (prev[fieldId] || []).filter(o => o !== opt),
                            }));
                          }}
                        >
                          Hapus
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {isAddRemoveEnabled && (
              <div className="p-3 border-top d-flex gap-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Tambah pilihan baru..."
                  value={customInputTexts[fieldId] || ""}
                  onChange={e => {
                    const txt = e.target.value;
                    setCustomInputTexts(prev => ({ ...prev, [fieldId]: txt }));
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm flex-shrink-0"
                  onClick={() => {
                    const text = (customInputTexts[fieldId] || "").trim();
                    if (!text) return;
                    if (displayOptions.includes(text)) {
                      alert("Pilihan sudah ada");
                      return;
                    }
                    setCustomOptions(prev => ({
                      ...prev,
                      [fieldId]: [...(prev[fieldId] || []), text],
                    }));
                    setCustomInputTexts(prev => ({ ...prev, [fieldId]: "" }));
                  }}
                >
                  + Tambah
                </button>
              </div>
            )}
            {isMultiple && (
              <div className="modal-footer p-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100"
                  onClick={() => setDropdownModalData(null)}
                >
                  Selesai
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderField = (field: any) => {
    if (hiddenFields.has(field.id)) return null;
    const { id, label, type, options, attachmentUrl, actionRules } = field;
    const fieldType = type.value;

    switch (fieldType) {
      case "Text": {
        const isSimple = (field.displayType || "full").toLowerCase() === "simple";
        const hasAttachment = attachmentUrl != null && attachmentUrl !== "";

        if (isSimple && hasAttachment) {
          return (
            <div key={id} className="bg-white p-3 mb-3 d-flex gap-3 align-items-center" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
              <img
                src={attachmentUrl}
                alt="attachment"
                style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", flexShrink: 0, cursor: "pointer" }}
                onClick={() => window.open(attachmentUrl, "_blank")}
              />
              <div className="flex-grow-1">
                <label className="form-label fw-semibold mb-1 small">{label}</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={values[id] || ""}
                  disabled={!isFormReady}
                  onFocus={() => {
                    if (!isDemoMode && isFormReadyRef.current) {
                      currentActiveFieldIdRef.current = id;
                    }
                  }}
                  onBlur={() => {
                    if (!isDemoMode && isFormReadyRef.current) {
                      endFieldAndLog(id);
                      if (currentActiveFieldIdRef.current === id) currentActiveFieldIdRef.current = null;
                    }
                  }}
                  onChange={e => handleChange(id, e.target.value, field)}
                />
              </div>
            </div>
          );
        }

        return (
          <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}>
            {attachmentUrl && <img src={attachmentUrl} className="mb-2" style={{ width: "100%", maxHeight: "300px" }} />}
            <label className="form-label fw-semibold">{label}</label>
            <input
              type="text"
              className="form-control"
              value={values[id] || ""}
              disabled={!isFormReady}
              onFocus={() => {
                if (!isDemoMode && isFormReadyRef.current) {
                  currentActiveFieldIdRef.current = id;
                }
              }}
              onBlur={() => {
                if (!isDemoMode && isFormReadyRef.current) {
                  endFieldAndLog(id);
                  if (currentActiveFieldIdRef.current === id) currentActiveFieldIdRef.current = null;
                }
              }}
              onChange={e => handleChange(id, e.target.value, field)}
            />
          </div>
        );
      }

      case "Number": {
        const rawValue = values[id] || "";
        const displayValue = rawValue ? String(rawValue) : "";
        const isSimple = (field.displayType || "full").toLowerCase() === "simple";
        const hasAttachment = attachmentUrl != null && attachmentUrl !== "";

        if (isSimple && hasAttachment) {
          return (
            <div key={id} className="bg-white p-3 mb-3 d-flex gap-3 align-items-center" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
              <img
                src={attachmentUrl}
                alt="attachment"
                style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", flexShrink: 0, cursor: "pointer" }}
                onClick={() => window.open(attachmentUrl, "_blank")}
              />
              <div className="flex-grow-1">
                <label className="form-label fw-semibold mb-1 small">{label}</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={displayValue}
                  disabled={!isFormReady}
                  onFocus={() => {
                    if (!isDemoMode && isFormReadyRef.current) {
                      currentActiveFieldIdRef.current = id;
                    }
                  }}
                  onBlur={() => {
                    if (!isDemoMode && isFormReadyRef.current) {
                      endFieldAndLog(id);
                      if (currentActiveFieldIdRef.current === id) currentActiveFieldIdRef.current = null;
                    }
                  }}
                  onChange={e => handleChange(id, e.target.value, field)}
                />
              </div>
            </div>
          );
        }

        return (
          <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}>
            {attachmentUrl && <img src={attachmentUrl} className="mb-2" style={{ width: "100%", maxHeight: "300px" }} />}
            <label className="form-label fw-semibold">{label}</label>
            <input
              type="number"
              className="form-control"
              value={displayValue}
              disabled={!isFormReady}
              onFocus={() => {
                if (!isDemoMode && isFormReadyRef.current) {
                  currentActiveFieldIdRef.current = id;
                }
              }}
              onBlur={() => {
                if (!isDemoMode && isFormReadyRef.current) {
                  endFieldAndLog(id);
                  if (currentActiveFieldIdRef.current === id) currentActiveFieldIdRef.current = null;
                }
              }}
              onChange={e => handleChange(id, e.target.value, field)}
            />
          </div>
        );
      }

      case "Phone Number":
        return (
          <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}>
            {attachmentUrl && <img src={attachmentUrl} className="mb-2" style={{ width: "100%", maxHeight: "300px" }} />}
            <label className="form-label fw-semibold">{label}</label>
            <div className="d-flex gap-2">
              <input
                type="tel"
                className="form-control"
                value={values[id] || ""}
                disabled={!isFormReady}
                onFocus={() => {
                  if (!isDemoMode && isFormReadyRef.current) {
                    currentActiveFieldIdRef.current = id;
                  }
                }}
                onBlur={() => {
                  if (!isDemoMode && isFormReadyRef.current) {
                    endFieldAndLog(id);
                    if (currentActiveFieldIdRef.current === id) currentActiveFieldIdRef.current = null;
                  }
                }}
                onChange={e => {
                  const validatedText = validatePhoneNumber(e.target.value);
                  handleChange(id, validatedText, field);
                }}
              />
              <button
                type="button"
                className="btn btn-success d-flex align-items-center gap-2"
                disabled={!values[id] || !isFormReady}
                onClick={() => {
                  const phoneNumber = values[id];
                  if (!phoneNumber) return;

                  let cleanNumber = phoneNumber.replace(/\D/g, '');
                  if (cleanNumber.startsWith('0')) {
                    cleanNumber = '62' + cleanNumber.substring(1);
                  } else if (!cleanNumber.startsWith('62')) {
                    cleanNumber = '62' + cleanNumber;
                  }

                  window.open(`https://wa.me/${cleanNumber}`, '_blank');
                }}
              >
                Check
              </button>
            </div>
          </div>
        );

      case "Single Selection": {
        const predefinedOptions = (field.options || []).filter((opt: string) => opt !== "enable_add_remove_value");
        const isAddRemoveEnabled = (field.options || []).includes("enable_add_remove_value");
        const currentSelectedValue = values[id];
        const loadedCustomOptions = isAddRemoveEnabled && currentSelectedValue && !predefinedOptions.includes(currentSelectedValue) && currentSelectedValue !== "enable_add_remove_value"
          ? [currentSelectedValue]
          : [];
        const addedCustomOptions = customOptions[id] || [];
        const mergedCustomOptions = Array.from(new Set([...loadedCustomOptions, ...addedCustomOptions]));
        const displayOptions = [...predefinedOptions, ...mergedCustomOptions];

        const isSimple = (field.displayType || "full").toLowerCase() === "simple";
        const hasAttachment = attachmentUrl != null && attachmentUrl !== "";

        if (isSimple) {
          const dropdownTriggerNode = (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-between text-start"
              style={{ backgroundColor: "#fff", borderColor: "#ced4da" }}
              disabled={!isFormReady}
              onClick={() => {
                if (!isFormReady) return;
                openDropdownModal(field, displayOptions, mergedCustomOptions, isAddRemoveEnabled, false);
              }}
            >
              <span className={`text-truncate ${!values[id] ? "text-muted" : "fw-medium text-dark"}`}>
                {values[id] || "Pilih jawaban..."}
              </span>
              <span className="ms-2">▼</span>
            </button>
          );

          if (hasAttachment) {
            return (
              <div key={id} className="bg-white p-3 mb-3 d-flex gap-3 align-items-center" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
                <img
                  src={attachmentUrl}
                  alt="attachment"
                  style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", flexShrink: 0, cursor: "pointer" }}
                  onClick={() => setPreviewAttachmentUrl(attachmentUrl)}
                />
                <div className="flex-grow-1">
                  <label className="form-label fw-semibold mb-1 small">{label}</label>
                  {dropdownTriggerNode}
                </div>
              </div>
            );
          }

          return (
            <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
              <label className="form-label fw-semibold mb-2">{label}</label>
              {dropdownTriggerNode}
            </div>
          );
        }

        return (
          <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
            {attachmentUrl && <img src={attachmentUrl} className="mb-2" style={{ width: "100%", maxHeight: "300px", cursor: "pointer" }} onClick={() => setPreviewAttachmentUrl(attachmentUrl)} />}
            <label className="form-label fw-semibold mb-3">{label}</label>
            <div className="row g-2">
              {displayOptions.map((opt: string, i: number) => {
                const checked = values[id] === opt;
                return (
                  <div className="col-12" key={i}>
                    <div
                      className="p-2 d-flex align-items-center gap-2"
                      style={{
                        border: checked ? "1.5px solid #0d6efd" : "1px solid #dee2e6",
                        borderRadius: "6px",
                        backgroundColor: checked ? "#e7f1ff" : "#fff",
                        cursor: isFormReady ? "pointer" : "not-allowed",
                        opacity: isFormReady ? 1 : 0.6,
                      }}
                      onClick={() => {
                        if (!isFormReady) return;
                        if (!isDemoMode) endFieldAndLog(id);
                        handleChange(id, opt, field);
                      }}
                    >
                      <input className="form-check-input m-0" type="radio" name={id} checked={checked} readOnly />
                      <span className="small fw-medium">{opt}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case "Multiple Selection": {
        const predefinedOptions = (field.options || []).filter((opt: string) => opt !== "enable_add_remove_value");
        const isAddRemoveEnabled = (field.options || []).includes("enable_add_remove_value");
        const currentSelectedValues = Array.isArray(values[id]) ? values[id] : [];
        const loadedCustomOptions = isAddRemoveEnabled
          ? currentSelectedValues.filter((val: string) => !predefinedOptions.includes(val) && val !== "enable_add_remove_value")
          : [];
        const addedCustomOptions = customOptions[id] || [];
        const mergedCustomOptions = Array.from(new Set([...loadedCustomOptions, ...addedCustomOptions]));
        const displayOptions = [...predefinedOptions, ...mergedCustomOptions];

        const isSimple = (field.displayType || "full").toLowerCase() === "simple";
        const hasAttachment = attachmentUrl != null && attachmentUrl !== "";

        if (isSimple) {
          const selectedCount = currentSelectedValues.length;
          let displayText = "Pilih jawaban...";
          if (selectedCount === 1) {
            displayText = currentSelectedValues[0];
          } else if (selectedCount > 1) {
            displayText = `${selectedCount} opsi terpilih`;
          }

          const dropdownTriggerNode = (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-between text-start"
              style={{ backgroundColor: "#fff", borderColor: "#ced4da" }}
              disabled={!isFormReady}
              onClick={() => {
                if (!isFormReady) return;
                openDropdownModal(field, displayOptions, mergedCustomOptions, isAddRemoveEnabled, true);
              }}
            >
              <span className={`text-truncate ${selectedCount === 0 ? "text-muted" : "fw-medium text-dark"}`}>
                {displayText}
              </span>
              <span className="ms-2">▼</span>
            </button>
          );

          if (hasAttachment) {
            return (
              <div key={id} className="bg-white p-3 mb-3 d-flex gap-3 align-items-center" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
                <img
                  src={attachmentUrl}
                  alt="attachment"
                  style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", flexShrink: 0, cursor: "pointer" }}
                  onClick={() => setPreviewAttachmentUrl(attachmentUrl)}
                />
                <div className="flex-grow-1">
                  <label className="form-label fw-semibold mb-1 small">{label}</label>
                  {dropdownTriggerNode}
                </div>
              </div>
            );
          }

          return (
            <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
              <label className="form-label fw-semibold mb-2">{label}</label>
              {dropdownTriggerNode}
            </div>
          );
        }

        return (
          <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}>
            {attachmentUrl && <img src={attachmentUrl} className="mb-2" style={{ width: "100%", maxHeight: "300px", cursor: "pointer" }} onClick={() => setPreviewAttachmentUrl(attachmentUrl)} />}
            <label className="form-label fw-semibold mb-3">{label}</label>
            <div className="row g-2">
              {displayOptions.map((opt: string, i: number) => {
                const checked = values[id]?.includes(opt);
                return (
                  <div className="col-12" key={i}>
                    <div
                      className="p-2 d-flex align-items-center gap-2"
                      style={{
                        border: checked ? "1.5px solid #0d6efd" : "1px solid #dee2e6",
                        borderRadius: "6px",
                        backgroundColor: checked ? "#e7f1ff" : "#fff",
                        cursor: isFormReady ? "pointer" : "not-allowed",
                        opacity: isFormReady ? 1 : 0.6,
                      }}
                      onClick={() => {
                        if (!isFormReady) return;
                        if (!isDemoMode) {
                          if (multiSelectFieldIdRef.current !== id) {
                            if (multiSelectFieldIdRef.current !== null) endMultiSelectAndLog();
                            multiSelectFieldIdRef.current = id;
                            multiSelectStartTimeRef.current = nextFieldStartTimeRef.current;
                          }
                          if (multiSelectTimerRef.current) clearTimeout(multiSelectTimerRef.current);
                          multiSelectTimerRef.current = setTimeout(() => {
                            endMultiSelectAndLog();
                            multiSelectTimerRef.current = null;
                          }, 800);
                        }
                        handleCheckboxChange(id, opt, actionRules);
                      }}
                    >
                      <input className="form-check-input m-0" type="checkbox" checked={checked} readOnly />
                      <span className="small fw-medium">{opt}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case "Take Picture": {
        const currentPhotos = Array.isArray(values[id]) ? values[id] : values[id] ? [values[id]] : [];

        let exactRequired = 0;
        const minRefRegex = new RegExp(`field_min_value_by_reference\\("${id}",\\s*"([^"]+)"\\)`);
        if (currentPage?.actionRules) {
          const match = currentPage.actionRules.match(minRefRegex);
          if (match) {
            const refRaw = values[match[1]];
            const refStr = Array.isArray(refRaw) ? refRaw[0] : refRaw;
            const parsed = parseInt(refStr, 10);
            if (!isNaN(parsed)) exactRequired = parsed;
          }
        }
        exactRequired = exactRequired || 1;
        const isQuotaFull = currentPhotos.length >= exactRequired;

        return (
          <div key={id} className="bg-white p-3 mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}>
            {attachmentUrl && <img src={attachmentUrl} className="mb-2" style={{ width: "100%", maxHeight: "300px" }} />}
            <label className="form-label fw-semibold d-block mb-3">
              {label}
              {exactRequired > 1 && <span className="text-danger ms-1 small">({exactRequired} Foto)</span>}
            </label>

            {currentPhotos.length > 0 && (
              <div className="row g-2 mb-3">
                {currentPhotos.map((photoUrl: string, idx: number) => (
                  <div className="col-6 position-relative" key={idx}>
                    <img src={photoUrl} alt={`preview-${idx}`} className="img-thumbnail w-100" style={{ aspectRatio: "1/1", objectFit: "cover" }} />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm position-absolute"
                      style={{ top: "4px", right: "8px", borderRadius: "50%", padding: "2px 8px" }}
                      onClick={() => {
                        handleChange(id, currentPhotos.filter((_: any, i: number) => i !== idx), field);
                        setPhotoMethods(prev => ({
                          ...prev,
                          [id]: (prev[id] || []).filter((_, i) => i !== idx),
                        }));
                      }}
                    >&times;</button>
                  </div>
                ))}
              </div>
            )}

            {!isQuotaFull && (
              <button type="button" className="btn btn-outline-primary" onClick={() => handlePicturePress(id, field.methodCamera, field.methodGallery, field.availableRatioCameraInApps)} disabled={!isFormReady}>
                {currentPhotos.length > 0 ? "+ Tambah Foto Lain" : "Ambil Foto"}
              </button>
            )}
          </div>
        );
      }

      case "Matrix Reference": {
        const sourceField = findSourceField(id);
        if (!sourceField) {
          return (
            <div key={id} className="bg-white p-3 mb-3 border rounded">
              {label && label !== "-" && <label className="form-label fw-semibold">{label}</label>}
              <div className="text-danger small fst-italic">Source field reference tidak ditemukan</div>
            </div>
          );
        }

        const sourceValue = values[sourceField.id];
        const selectedOptions = Array.isArray(sourceValue) ? sourceValue : (sourceValue ? [sourceValue] : []);

        if (selectedOptions.length === 0) return null;

        return (
          <div key={id} className="bg-white p-3 mb-3 border rounded">
            {label && label !== "-" && <label className="form-label fw-semibold mb-3">{label}</label>}
            <div className="bg-light p-3 rounded border d-flex flex-column gap-3">
              {(options || []).map((optPattern: string, optIdx: number) => {
                const match = optPattern.match(/^(.*)\s*\[(\d+)\]$/);
                if (!match) return null;
                const labelTemplate = match[1].trim();
                const subFieldTypeKey = parseInt(match[2], 10);

                return selectedOptions.map((opt: string, selIdx: number) => {
                  const subLabel = labelTemplate.replace("$option", opt);
                  const subValue = values[id]?.[subLabel] || "";

                  return (
                    <div key={`${optIdx}-${selIdx}`} className="d-flex flex-column gap-1">
                      <label className="form-label small fw-semibold text-secondary mb-1">{subLabel}</label>
                      {renderSubFieldInput(id, subLabel, subFieldTypeKey, subValue, field)}
                    </div>
                  );
                });
              })}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (isMoreAccessMode && (locationStatus === "requesting" || locationStatus === "idle") && !formData) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 gap-3 text-center px-4">
        <div className="spinner-border text-primary" />
        <span className="text-muted">Meminta akses lokasi...</span>
      </div>
    );
  }

  if (isMoreAccessMode && locationStatus === "denied") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 text-center px-4">
        <div className="text-danger fw-semibold mb-2">Akses lokasi diperlukan</div>
        <div className="text-muted small mb-3">Mohon izinkan akses lokasi pada browser untuk melanjutkan.</div>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Coba Lagi</button>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (!isFormReady && !isDemoMode && !isLoadingData && formData) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 gap-3">
        <div className="spinner-border text-primary" />
        <span className="text-muted">Memuat form...</span>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ paddingBottom: 100 }}>
      <h1 className="fw-bold mb-4">{currentPage?.name || formData?.title}</h1>

      {formData?.instruction && (
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-3"
          onClick={() => setShowInstructionModal(true)}
        >
          Lihat Instruksi
        </button>
      )}

      {isMoreAccessMode && (
        <div className="alert alert-warning mb-3 py-2 small d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ borderRadius: "8px" }}>
          <span>Anda sedang mengerjakan tugas via Web Form. Pengerjaan via aplikasi mobile lebih disarankan.</span>
          <button
            type="button"
            className="btn btn-primary btn-sm fw-semibold text-nowrap"
            onClick={openAppWithDraftState}
          >
            Kembali ke Aplikasi
          </button>
        </div>
      )}

      {isDemoMode && (
        <div className="alert alert-info mb-3 py-2 small" style={{ borderRadius: "8px" }}>
          Ini adalah mode uji coba. Data yang dimasukkan tidak akan disimpan.
        </div>
      )}

      <div className="slide-container">
        <div key={currentPageId} className={`slide-page ${slideDirection === "left" ? "slide-in-left" : "slide-in-right"}`}>
          {currentPage?.fields.map((field: any) => renderField(field))}
        </div>
      </div>

      {showCameraModal && (
        <div className="dedicated-camera-fullscreen">
          <div className="dedicated-camera-topbar">
            <button
              className="dedicated-camera-close-btn"
              onClick={() => { stopCamera(); setShowCameraModal(false); }}
            >
              ✕
            </button>
          </div>

          <div className="dedicated-camera-preview-wrapper">
            <video
              id="camera-video"
              className="dedicated-camera-video"
              autoPlay
              playsInline
              muted
              ref={video => { if (video && cameraStream) video.srcObject = cameraStream; }}
            />
            <CropOverlay aspectRatioKey={aspectRatioKey} aspectRatios={ASPECT_RATIOS} />
          </div>

          <div className="dedicated-camera-ratio-tabs">
            {ASPECT_RATIOS.filter(r => parseAllowedAspectRatios(selectedFieldRatio).includes(r.key)).map(r => (
              <button
                key={r.key}
                className="dedicated-camera-ratio-tab"
                onClick={() => setAspectRatioKey(r.key)}
              >
                <span className={aspectRatioKey === r.key ? "ratio-tab-text-active" : "ratio-tab-text"}>
                  {r.label}
                </span>
                {aspectRatioKey === r.key && <div className="ratio-tab-indicator" />}
              </button>
            ))}
          </div>

          <div className="dedicated-camera-bottom-bar">
            <button className="dedicated-camera-capture-btn" onClick={capturePhoto}>
              <div className="dedicated-camera-capture-btn-inner" />
            </button>
            <button className="dedicated-camera-flip-btn" onClick={switchCameraFacing}>
              ⟳
            </button>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-0 overflow-hidden">
              <div className="text-center py-2 fw-semibold border-bottom">Preview Foto</div>
              <div className="d-flex justify-content-center py-3">
                <div
                  className="camera-square"
                  style={{ aspectRatio: ASPECT_RATIOS.find(r => r.key === aspectRatioKey)?.ratio ?? 1 }}
                >
                  <img src={previewUrl!} className="camera-preview" alt="Preview" />
                </div>
              </div>

              <div className="d-flex gap-2 p-3 border-top">
                <button className="btn btn-outline-secondary" onClick={retakePhoto}>Ambil ulang</button>
                <button className="btn btn-primary flex-grow-1" onClick={uploadCapturedPhoto} disabled={uploadingPhoto}>
                  {uploadingPhoto ? <span className="spinner-border spinner-border-sm" /> : "Upload Foto"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMethodModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-0 overflow-hidden">
              <div className="text-center py-2 fw-semibold border-bottom">Upload Gambar</div>
              <div className="d-flex flex-column gap-2 p-3">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowMethodModal(false);
                    if (methodFieldId) openCamera(methodFieldId, selectedFieldRatio);
                  }}
                >
                  Foto
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setShowMethodModal(false);
                    if (methodFieldId) openGallery(methodFieldId);
                  }}
                >
                  Galeri
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInstructionModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title fw-semibold mb-0">Instruksi</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowInstructionModal(false)}
                />
              </div>
              <div className="modal-body">
                <div dangerouslySetInnerHTML={{ __html: formData?.instruction || "" }} />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={() => setShowInstructionModal(false)}
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderDropdownSelectionModal()}

      {previewAttachmentUrl && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.8)", zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0 pb-0 justify-content-end">
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setPreviewAttachmentUrl(null)}
                />
              </div>
              <div className="modal-body text-center p-2">
                <img
                  src={previewAttachmentUrl}
                  alt="attachment preview"
                  style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "8px" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {uploadingPhoto && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ background: "rgba(255,255,255,0.7)", zIndex: 2000 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" />
            <div className="mt-2 fw-semibold">Uploading...</div>
          </div>
        </div>
      )}

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleGalleryFileSelected}
      />

      <div
        className="border-top p-3 d-flex justify-content-between align-items-center"
        style={{ backgroundColor: "#fff" }}
      >
        {pageStack.length > 0 ? (
          <button
            className="btn btn-light d-flex align-items-center gap-2"
            onClick={handleBackPage}
            style={{ border: "1px solid #dee2e6" }}
          >
            <span style={{ fontSize: "18px" }}>←</span>
            Kembali
          </button>
        ) : (
          <div />
        )}

        <button
          className="btn btn-primary d-flex align-items-center gap-2 px-4"
          disabled={isSubmitting || !isFormReady}
          style={{ minHeight: "44px", borderRadius: "999px" }}
          onClick={() => {
            const now = Date.now();
            if (now - lastSubmitTapRef.current < SUBMIT_THROTTLE_MS) return;
            lastSubmitTapRef.current = now;

            if (!validateCurrentPage()) return;

            const page = formData?.pages?.find((p: any) => p.id === currentPageId);
            if (page?.actionRules) {
              handleActionRules(page.actionRules, values, "page");
            } else {
              endMultiSelectAndLog();
              if (currentActiveFieldIdRef.current) {
                endFieldAndLog(currentActiveFieldIdRef.current);
                currentActiveFieldIdRef.current = null;
              }
              if (isDemoMode) navigateToSummary();
              else checkAndNavigateToSummary();
            }
          }}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
              Submitting...
            </>
          ) : pagePrimaryAction === "next" ? (
            <>
              Lanjut
              <span style={{ fontSize: "18px" }}>→</span>
            </>
          ) : (
            <>
              Kirim
              <span style={{ fontSize: "18px" }}>✓</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const CropOverlay: React.FC<{
  aspectRatioKey: '1:1' | '9:16' | '16:9';
  aspectRatios: { key: string; ratio: number }[];
}> = ({ aspectRatioKey, aspectRatios }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const updateLayout = () => {
      const el = document.querySelector(".dedicated-camera-preview-wrapper");
      if (el) setLayout({ width: el.clientWidth, height: el.clientHeight });
    };
    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  if (!layout) return <div ref={wrapperRef} style={{ position: "absolute", inset: 0 }} />;

  const ratioConfig = aspectRatios.find(r => r.key === aspectRatioKey)!;
  const { width: pw, height: ph } = layout;

  let boxWidth = pw;
  let boxHeight = pw / ratioConfig.ratio;
  if (boxHeight > ph) {
    boxHeight = ph;
    boxWidth = ph * ratioConfig.ratio;
  }

  const top = (ph - boxHeight) / 2;
  const left = (pw - boxWidth) / 2;

  return (
    <div ref={wrapperRef} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {top > 0.5 && (
        <>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: top, background: "rgba(0,0,0,0.45)" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: top, background: "rgba(0,0,0,0.45)" }} />
        </>
      )}
      {left > 0.5 && (
        <>
          <div style={{ position: "absolute", top, bottom: top, left: 0, width: left, background: "rgba(0,0,0,0.45)" }} />
          <div style={{ position: "absolute", top, bottom: top, right: 0, width: left, background: "rgba(0,0,0,0.45)" }} />
        </>
      )}
      <div
        style={{
          position: "absolute",
          top, left, width: boxWidth, height: boxHeight,
          border: "1px solid rgba(255,255,255,0.9)",
        }}
      >
        <div className="crop-corner crop-corner-tl" />
        <div className="crop-corner crop-corner-tr" />
        <div className="crop-corner crop-corner-bl" />
        <div className="crop-corner crop-corner-br" />
      </div>
    </div>
  );
};

export default TaskFormPage;